import React, { Suspense, lazy } from 'react';
import { HashRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { OfflineProvider } from './context/OfflineContext';
import SubscriptionGate from './components/SubscriptionGate';
import './i18n';
import Layout from './components/Layout';
import Spinner from './components/ui/Spinner';

/* ── ChunkLoadError boundary ─────────────────────────────────────────
   Catches "Loading chunk X failed" errors when a lazy-loaded JS chunk
   is not in service worker cache and network is unavailable.
─────────────────────────────────────────────────────────────────────── */
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    const isChunk =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Importing a module script failed');
    return { hasError: true, isChunkError: isChunk, error };
  }
  componentDidCatch(error) {
    console.warn('[EasyFacture] ChunkErrorBoundary caught:', error?.message);
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.state.isChunkError) {
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: '100vh', fontFamily: 'Arial, sans-serif',
          background: '#f8fafc', color: '#334155', padding: '24px', textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
          <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>
            Page non disponible hors connexion
          </h2>
          <p style={{ color: '#64748b', marginBottom: '24px', maxWidth: '360px' }}>
            Cette page n'a pas encore été mise en cache. Connectez-vous à Internet pour la charger une première fois.
          </p>
          <button
            onClick={() => { this.setState({ hasError: false, error: null }); window.history.back(); }}
            style={{ background: '#6366f1', color: 'white', border: 'none', borderRadius: '12px',
              padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', marginRight: '8px' }}>
            ← Retour
          </button>
          <button
            onClick={() => window.location.reload()}
            style={{ background: '#e0e7ff', color: '#4f46e5', border: 'none', borderRadius: '12px',
              padding: '10px 24px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>
            Réessayer
          </button>
        </div>
      );
    }
    return null;
  }
}

// Lazy load pages
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const DemoRegister = lazy(() => import('./pages/DemoRegister'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Invoices = lazy(() => import('./pages/Invoices'));
const InvoiceForm = lazy(() => import('./pages/InvoiceForm'));
const InvoiceView = lazy(() => import('./pages/InvoiceView'));
const Quotes = lazy(() => import('./pages/Quotes'));
const QuoteForm = lazy(() => import('./pages/QuoteForm'));
const QuoteView = lazy(() => import('./pages/QuoteView'));
const Clients = lazy(() => import('./pages/Clients'));
const ClientForm = lazy(() => import('./pages/ClientForm'));
const ClientView = lazy(() => import('./pages/ClientView'));
const Products = lazy(() => import('./pages/Products'));
const Deliveries = lazy(() => import('./pages/Deliveries'));
const DeliveryForm = lazy(() => import('./pages/DeliveryForm'));
const LettersOfChange = lazy(() => import('./pages/LettersOfChange'));
const LetterOfChangeForm = lazy(() => import('./pages/LetterOfChangeForm'));
const Returns = lazy(() => import('./pages/Returns'));
const ReturnForm = lazy(() => import('./pages/ReturnForm'));
const Settings = lazy(() => import('./pages/Settings'));
const POS = lazy(() => import('./pages/POS'));
const Stock = lazy(() => import('./pages/Stock'));
const SubUsers = lazy(() => import('./pages/SubUsers'));
const POSReport = lazy(() => import('./pages/POSReport'));
const ClientCredits = lazy(() => import('./pages/ClientCredits'));
const BusinessExpenses = lazy(() => import('./pages/BusinessExpenses'));

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>;
  return user ? children : <Navigate to="/login" />;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>;
  return !user ? children : <Navigate to="/" />;
};

// Permissions bloquées pour les comptes Point de vente
const POS_BLOCKED_PERMS = new Set(['dashboard', 'invoices', 'quotes', 'deliveries', 'returns', 'lettresChange']);

// Vérifie la permission pour les sous-utilisateurs ET le type de compte POS
const PermRoute = ({ perm, children }) => {
  const { user } = useAuth();
  // Compte POS principal → bloquer les modules non-POS
  if (user?.userType === 'pos' && perm && POS_BLOCKED_PERMS.has(perm)) {
    return <Navigate to="/pos" replace />;
  }
  // Compte principal standard → accès total
  if (!user?.isSubUser) return children;
  // Sous-utilisateur → vérifie la permission
  const perms = user.permissions || {};
  if (perms[perm]) return children;
  return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
      <div className="text-5xl mb-3">🔒</div>
      <p className="font-semibold text-slate-600 dark:text-slate-300">Accès non autorisé</p>
      <p className="text-sm mt-1">Vous n'avez pas accès à cette section</p>
    </div>
  );
};

// Redirige vers la bonne page d'accueil selon le type de compte
const HomeRedirect = () => {
  const { user } = useAuth();
  // Compte POS → page caisse directement
  if (user?.userType === 'pos') return <Navigate to="/pos" replace />;
  if (!user?.isSubUser) return <Dashboard />;
  const perms = user.permissions || {};
  const order = ['dashboard','pos','invoices','quotes','clients','credits','products','stock','deliveries','returns','suppliers','posReport','lettresChange'];
  const routes = { dashboard:'/', pos:'/pos', invoices:'/invoices', quotes:'/quotes', clients:'/clients', credits:'/client-credits', products:'/products', stock:'/stock', deliveries:'/deliveries', returns:'/returns', suppliers:'/suppliers', posReport:'/pos-report', lettresChange:'/letters-of-change' };
  const first = order.find(p => perms[p]);
  if (!first) return (
    <div className="flex flex-col items-center justify-center h-64 text-slate-400">
      <div className="text-5xl mb-3">🔒</div>
      <p className="font-semibold text-slate-600 dark:text-slate-300">Aucun accès configuré</p>
      <p className="text-sm mt-1">Contactez votre administrateur</p>
    </div>
  );
  if (first === 'dashboard') return <Dashboard />;
  return <Navigate to={routes[first]} replace />;
};

function AppRoutes() {
  return (
    <ChunkErrorBoundary>
    <Suspense fallback={<div className="flex items-center justify-center h-screen"><Spinner size="lg" /></div>}>
      <Routes>
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
        <Route path="/demo" element={<PublicRoute><DemoRegister /></PublicRoute>} />
        <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
          <Route index element={<HomeRedirect />} />
          {/* Invoices */}
          <Route path="invoices" element={<PermRoute perm="invoices"><Invoices /></PermRoute>} />
          <Route path="invoices/new" element={<PermRoute perm="invoices"><InvoiceForm /></PermRoute>} />
          <Route path="invoices/:id" element={<PermRoute perm="invoices"><InvoiceView /></PermRoute>} />
          <Route path="invoices/:id/edit" element={<PermRoute perm="invoices"><InvoiceForm /></PermRoute>} />
          {/* Credits */}
          <Route path="credits" element={<PermRoute perm="invoices"><Invoices type="credit" /></PermRoute>} />
          <Route path="credits/new" element={<PermRoute perm="invoices"><InvoiceForm type="credit" /></PermRoute>} />
          <Route path="credits/:id" element={<PermRoute perm="invoices"><InvoiceView /></PermRoute>} />
          <Route path="credits/:id/edit" element={<PermRoute perm="invoices"><InvoiceForm type="credit" /></PermRoute>} />
          {/* Quotes */}
          <Route path="quotes" element={<PermRoute perm="quotes"><Quotes /></PermRoute>} />
          <Route path="quotes/new" element={<PermRoute perm="quotes"><QuoteForm /></PermRoute>} />
          <Route path="quotes/:id" element={<PermRoute perm="quotes"><QuoteView /></PermRoute>} />
          <Route path="quotes/:id/edit" element={<PermRoute perm="quotes"><QuoteForm /></PermRoute>} />
          {/* Clients */}
          <Route path="clients" element={<PermRoute perm="clients"><Clients /></PermRoute>} />
          <Route path="clients/new" element={<PermRoute perm="clients"><ClientForm /></PermRoute>} />
          <Route path="clients/:id" element={<PermRoute perm="clients"><ClientView /></PermRoute>} />
          <Route path="clients/:id/edit" element={<PermRoute perm="clients"><ClientForm /></PermRoute>} />
          {/* Products */}
          <Route path="products" element={<PermRoute perm="products"><Products /></PermRoute>} />
          {/* Deliveries */}
          <Route path="deliveries" element={<PermRoute perm="deliveries"><Deliveries /></PermRoute>} />
          <Route path="deliveries/new" element={<PermRoute perm="deliveries"><DeliveryForm /></PermRoute>} />
          <Route path="deliveries/:id/edit" element={<PermRoute perm="deliveries"><DeliveryForm /></PermRoute>} />

          {/* Letters of change */}
          <Route path="letters-of-change" element={<PermRoute perm="lettresChange"><LettersOfChange /></PermRoute>} />
          <Route path="letters-of-change/new" element={<PermRoute perm="lettresChange"><LetterOfChangeForm /></PermRoute>} />
          <Route path="letters-of-change/:id/edit" element={<PermRoute perm="lettresChange"><LetterOfChangeForm /></PermRoute>} />
          {/* Returns */}
          <Route path="returns" element={<PermRoute perm="returns"><Returns /></PermRoute>} />
          <Route path="returns/new" element={<PermRoute perm="returns"><ReturnForm /></PermRoute>} />
          <Route path="returns/:id/edit" element={<PermRoute perm="returns"><ReturnForm /></PermRoute>} />
          {/* Settings */}
          <Route path="settings" element={<PermRoute perm="settings"><Settings /></PermRoute>} />
          {/* POS */}
          <Route path="pos" element={<PermRoute perm="pos"><POS /></PermRoute>} />
          {/* Stock */}
          <Route path="stock" element={<PermRoute perm="stock"><Stock /></PermRoute>} />
          {/* Utilisateurs — owner only */}
          <Route path="users" element={<SubUsers />} />
          {/* Reporting POS */}
          <Route path="pos-report" element={<PermRoute perm="posReport"><POSReport /></PermRoute>} />
          {/* Crédits clients */}
          <Route path="client-credits" element={<PermRoute perm="credits"><ClientCredits /></PermRoute>} />
          <Route path="business-expenses" element={<PermRoute perm="expenses"><BusinessExpenses /></PermRoute>} />
          {/* Fournisseurs */}
          <Route path="suppliers" element={<PermRoute perm="suppliers"><Stock initialTab="suppliers" /></PermRoute>} />
          {/* Bons de commande */}
          <Route path="purchase-orders" element={<PermRoute perm="purchaseOrders"><Stock initialTab="purchase-orders" /></PermRoute>} />
        </Route>
      </Routes>
    </Suspense>
    </ChunkErrorBoundary>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <OfflineProvider>
          <SubscriptionGate>
            <Router>
              <AppRoutes />
              <Toaster position="top-right" toastOptions={{
                className: 'dark:bg-slate-800 dark:text-white',
                duration: 3000
              }} />
            </Router>
          </SubscriptionGate>
        </OfflineProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}